import { Badge, Tooltip } from 'antd';
import { Fragment } from 'react';
import { ExportOutlined } from '@ant-design/icons';
import { generatePath } from 'react-router-dom';
import { routes } from '$src/routes';
import { InfoOutlineIcon } from '@ring-icons';
import { Link } from '@amzn/ring-react-components/csr/components/Link';
import isWebView from '$src/utils/user-agent';

const ONBOARDING_COMPLETE_STATUS = 'complete';
const ONBOARDING_INCOMPLETE_STATUS = 'incomplete';

export const VSG_MONITORING_ELIGIBLE = 'VSG_MONITORING_ELIGIBLE';
export const VSG_MONITORING_INELIGIBLE = 'VSG_MONITORING_INELIGIBLE';
export const AUTH_NOT_FOUND = 'AUTH_NOT_FOUND';
export const ENROLLED_DEVICE_NOT_FOUND = 'ENROLLED_DEVICE_NOT_FOUND';
export const ENTITLEMENT_NOT_FOUND = 'ENTITLEMENT_NOT_FOUND';
export const EVENT_OVERAGES_THRESHOLD_EXCEEDED = 'EVENT_OVERAGES_THRESHOLD_EXCEEDED';
export const EVENT_OVERAGES_CAP_EXCEEDED = 'EVENT_OVERAGES_CAP_EXCEEDED';
export const PRACTICE_MODE = 'PRACTICE_MODE';
export const PENDING_MODE = 'PENDING_MODE';
export const PROFESSIONAL_MONITORING_NOT_ENABLED = 'PROFESSIONAL_MONITORING_NOT_ENABLED';
export const SOS_OFF = 'SOS_OFF';
export const SOS_SETUP_INCOMPLETE = 'SOS_SETUP_INCOMPLETE';
export const SETUP_INCOMPLETE = 'SETUP_INCOMPLETE';
export const UNKNOWN = 'UNKNOWN';

export const eligibilityStatus = {
  [VSG_MONITORING_ELIGIBLE]: {
    xs: 'Eligible',
    md: 'Location is eligible for VSG',
    lg: 'Location is eligible for Virtual Security Guard',
  },
  [VSG_MONITORING_INELIGIBLE]: {
    xs: 'Ineligible',
    md: 'Location is not eligible for VSG',
    lg: 'Location is not eligible for Virtual Security Guard',
  },
  [AUTH_NOT_FOUND]: {
    xs: 'Not Authorized',
    md: 'Location is not authorized for VSG',
    lg: 'Complete authorization and re-arm your system to activate Virtual Security Guard',
  },
  [ENROLLED_DEVICE_NOT_FOUND]: {
    xs: 'No Devices',
    md: 'No enrolled devices',
    lg: 'Enroll at least one eligible device to activate Virtual Security Guard',
  },
  [ENTITLEMENT_NOT_FOUND]: {
    xs: 'No Subscription',
    md: 'Location is not subscribed to VSG',
    lg: 'Complete subscription to activate Virtual Security Guard',
  },
  [EVENT_OVERAGES_THRESHOLD_EXCEEDED]: {
    xs: 'Event Threshold Exceeded',
    md: 'Event overages threshold exceeded',
    lg: "Opt in to Extra Events to keep Virtual Security Guard active after you've used all event included events each month",
  },
  [EVENT_OVERAGES_CAP_EXCEEDED]: {
    xs: 'Event Cap Exceeded',
    md: 'Event overages cap exceeded',
    lg: 'Guards will monitor up to Extra Events in this billing cycle for $0.99 per event',
  },
  [PRACTICE_MODE]: {
    xs: 'Practice Mode Enabled',
    md: 'Practice Mode Enabled',
    lg: 'End alarm practice, switch to Professional Monitoring, and re-arm your alarm to activate Virtual Security Guard',
  },
  [PENDING_MODE]: {
    xs: 'Pending Mode Enabled',
    md: 'Alarm Professional Monitoring or SOS is not active',
    lg: 'Turn on alarm Professional Monitoring or SOS and re-arm your system to activate Virtual Security Guard',
  },
  [PROFESSIONAL_MONITORING_NOT_ENABLED]: {
    xs: 'Monitoring Disabled',
    md: 'Professional Monitoring disabled',
    lg: 'Turn on alarm Professional Monitoring and re-arm your system to activate Virtual Security Guard',
  },
  [SOS_OFF]: {
    xs: 'SOS Off',
    md: 'SOS is off',
    lg: 'Turn on SOS and re-arm your location to activate Virtual Security Guard',
  },
  [SOS_SETUP_INCOMPLETE]: {
    xs: 'SOS Setup Incomplete',
    md: 'SOS Setup incomplete',
    lg: 'Complete SOS setup to activate Virtual Security Guard',
  },
  [SETUP_INCOMPLETE]: {
    xs: 'Setup Incomplete',
    md: 'Complete setup to activate VSG',
    lg: 'Complete setup to activate Virtual Security Guard',
  },
  [UNKNOWN]: {
    xs: 'Unknown',
    md: 'VSG status is unknown for this location',
    lg: 'Virtual Security Guard status is unknown for this location',
  },
};

export const monitoringStatus = {
  ACTIVE: {
    xs: 'Active',
    md: 'VSG is active for this location',
    lg: 'Virtual Security Guard is active for this location',
  },
  PAUSED: {
    xs: 'Paused',
    md: 'VSG is paused for this location',
    lg: 'Virtual Security Guard is paused. Guards will not monitor motion events on your enrolled cameras when you arm your system to Home or Away',
  },
  DISARMED: {
    xs: 'Disarmed',
    md: 'VSG is disarmed for this location',
    lg: 'Virtual Security Guard is disarmed for this location',
  },
  SNOOZED: {
    xs: 'Snoozed',
    md: 'VSG is snoozed for this location',
    lg: 'Virtual Security Guard is snoozed. Guards will not monitor motion events on your enrolled cameras when you arm your system to Home or Away',
  },
  INELIGIBLE: {
    xs: 'Ineligible',
    md: 'VSG is ineligible for this location',
    lg: 'Virtual Security Guard is ineligible for this location',
  },
  UNKNOWN: {
    xs: 'Unknown',
    md: 'VSG status is unknown for this location',
    lg: 'Virtual Security Guard status is unknown for this location',
  },
};

export const getColumns = (onLocationDetailsClicked) => [
  {
    title: 'Location',
    dataIndex: 'locationName',
    ellipsis: true,
    className: 'locations-table-card__name-column--mobile',
    sorter: (a, b) => a.locationName.localeCompare(b.locationName),
    responsive: ['xs'],
    render: (_, { locationId, locationName, onboardStatus, monitoring, monitoringReason, eligible, eligibilityReason, fullAddress, address }) => {
      const monitoringReasonMessage = monitoringStatus[monitoringReason] ? monitoringStatus[monitoringReason] : monitoringStatus.UNKNOWN;
      const eligibilityReasonMessage = eligibilityStatus[eligibilityReason] ? eligibilityStatus[eligibilityReason] : eligibilityStatus.UNKNOWN;
 
      return (
        <Fragment>
          <Link
            color="primary"
            type="external"
            onClick={() => {
              const link = generatePath(onboardStatus === ONBOARDING_COMPLETE_STATUS ? routes.settings : routes.onboardingIntroduction, {
                locationId,
              });

              if (isWebView()) {
                window.location.href = link;
              } else {
                window.open(link, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            <ExportOutlined />
            {locationName}
          </Link>
          <Tooltip title={`${fullAddress}, ${address?.city}`}>
            <b>Address:</b>
            <span className='locations-table-card__name-column--mobile--address'>{`${fullAddress}, ${address?.city}`}</span>
          </Tooltip>
          <span>
            <b>Onboarding:</b>
            {onboardStatus === ONBOARDING_COMPLETE_STATUS ? (
              <Badge status="success" text={'Complete'} />
            ) : (
              <Badge status="error" text={'Incomplete'} />
            )}
          </span>
          <Tooltip title={monitoringReasonMessage.md}>
            <b>Monitoring: </b> <Badge status={monitoring ? 'success' : 'error'} text={monitoringReasonMessage.xs} />
          </Tooltip>
          <Tooltip title={eligibilityReasonMessage.md}>
            <b>Eligibility: </b> <Badge status={eligible ? 'success' : 'error'} text={eligibilityReasonMessage.xs} />
          </Tooltip>
        </Fragment>
      );
    },
  },
  {
    title: 'Name',
    dataIndex: 'locationName',
    ellipsis: true,
    width: '20%',
    responsive: ['sm'],
    className: 'locations-table-card__name-column',
    sorter: (a, b) => a.locationName.localeCompare(b.locationName),
    render: (_, { locationId, locationName, onboardStatus }) => (
      <Link
        color="primary"
        link={generatePath(onboardStatus === ONBOARDING_COMPLETE_STATUS ? routes.settings : routes.onboardingIntroduction, { locationId })}
        type="external"
      >
        <ExportOutlined />
        {locationName}
      </Link>
    ),
  },
  {
    title: 'Address',
    dataIndex: 'address',
    ellipsis: true,
    width: '35%',
    responsive: ['sm'],
    sorter: (a, b) => a.address?.address1?.localeCompare(b.address?.address1),
    render: (_, { address, fullAddress }) => <Tooltip title={`${fullAddress}, ${address?.city}`}>{`${fullAddress}, ${address?.city}`}</Tooltip>,
  },
  {
    title: 'Onboarding',
    dataIndex: 'onboardStatus',
    width: '15%',
    responsive: ['sm'],
    filters: [
      {
        text: 'Complete',
        value: ONBOARDING_COMPLETE_STATUS,
      },
      {
        text: 'Incomplete',
        value: ONBOARDING_INCOMPLETE_STATUS,
      },
    ],
    ellipsis: true,
    onFilter: (value, location) => location.onboardStatus.indexOf(value) === 0,
    sorter: (a, b) => a.onboardStatus.localeCompare(b.onboardStatus),
    render: (_, { onboardStatus }) => onboardStatus === ONBOARDING_COMPLETE_STATUS ? <Badge status="success" text="Complete" /> : <Badge status="error" text="Incomplete" />,
  },
  {
    title: 'Monitoring',
    dataIndex: 'monitoring',
    width: '15%',
    responsive: ['sm'],
    filters: [
      {
        text: 'Active',
        value: true,
      },
      {
        text: 'Inactive',
        value: false,
      },
    ],
    ellipsis: true,
    sorter: (a, b) => {
      if (!a.monitoring && !b.monitoring) {
        const monitoringReasonA = monitoringStatus[a.monitoringReason] ? monitoringStatus[a.monitoringReason].xs : monitoringStatus.UNKNOWN.xs;
        const monitoringReasonB = monitoringStatus[b.monitoringReason] ? monitoringStatus[b.monitoringReason].xs : monitoringStatus.UNKNOWN.xs;

        return monitoringReasonA.localeCompare(monitoringReasonB);
      } if (a.monitoring === b.monitoring) return 0;

      return a.monitoring === true ? -1 : 1;
    },
    onFilter: (value, { monitoring }) => monitoring === value || (monitoring === undefined && !value),
    render: (_, { monitoring, monitoringReason }) => {
      const reason = monitoringStatus[monitoringReason] ? monitoringStatus[monitoringReason] : monitoringStatus.UNKNOWN;

      return (
        <Tooltip title={reason.md}>
          <Badge status={monitoring ? 'success' : 'error'} text={reason.xs} />
        </Tooltip>
      );
    },
  },
  {
    title: 'Eligibility',
    dataIndex: 'eligible',
    width: '15%',
    responsive: ['sm'],
    filters: [
      {
        text: 'Eligible',
        value: true,
      },
      {
        text: 'Not Eligible',
        value: false,
      },
    ],
    ellipsis: true,
    onFilter: (value, { eligible }) => eligible === value || (eligible === undefined && !value),
    sorter: (a, b) => {
      if (!a.eligible && !b.eligible) {
        const eligibilityReasonA = eligibilityStatus[a.eligibilityReason]
          ? eligibilityStatus[a.eligibilityReason].xs
          : eligibilityStatus.UNKNOWN.xs;
        const eligibilityReasonB = eligibilityStatus[b.eligibilityReason]
          ? eligibilityStatus[b.eligibilityReason].xs
          : eligibilityStatus.UNKNOWN.xs;

        return eligibilityReasonA.localeCompare(eligibilityReasonB);
      } if (a.eligible === b.eligible) return 0;

      return a.eligible === true ? -1 : 1;
    },
    render: (_, { eligible, eligibilityReason }) => {
      const reason = eligibilityStatus[eligibilityReason] ? eligibilityStatus[eligibilityReason] : eligibilityStatus.UNKNOWN;

      return (
        <Tooltip title={reason.md}>
          <Badge status={eligible ? 'success' : 'error'} text={reason.xs} />
        </Tooltip>
      );
    },
  },
  {
    title: 'Details',
    width: 80,
    className: 'locations-table-card__details-column',
    align: 'center',
    render: (_, location) => <InfoOutlineIcon onClick={() => onLocationDetailsClicked(location)} />,
  },
];
